export interface PostalCodeRule {
    pattern: RegExp;
    allowsLetters: boolean;
    maxLength: number;
    charPattern: RegExp;
}

const DIGITS = /^\d$/;
const DIGITS_OR_HYPHEN = /^[\d-]$/;
const DIGITS_OR_SPACE = /^[\d ]$/;
const ALPHANUMERIC = /^[a-zA-Z0-9 ]$/;
const ALPHANUMERIC_HYPHEN = /^[a-zA-Z0-9 -]$/;

const DEFAULT_RULE: PostalCodeRule = {
    pattern: /^\d{3,10}$/,
    allowsLetters: false,
    maxLength: 10,
    charPattern: DIGITS,
};

function numeric(length: number): PostalCodeRule {
    return {
        pattern: new RegExp(`^\\d{${length}}$`),
        allowsLetters: false,
        maxLength: length,
        charPattern: DIGITS,
    };
}

const NUMERIC_LENGTHS: Record<string, number> = {
    AT: 4, AU: 4, BD: 4, BE: 4, BG: 4, CH: 4, CY: 4, DK: 4, GE: 4, HU: 4, LI: 4, LU: 4, NO: 4, NZ: 4, PH: 4, SI: 4, TN: 4, VE: 4, ZA: 4,
    CR: 5, DE: 5, DZ: 5, EE: 5, EG: 5, ES: 5, FI: 5, FR: 5, GR: 5, HR: 5, ID: 5, IL: 7, IQ: 5, IT: 5, KE: 5, KH: 5, KR: 5, LK: 5, LT: 5,
    MA: 5, MM: 5, MX: 5, MY: 5, NP: 5, PE: 5, PK: 5, SA: 5, TH: 5, TR: 5, TW: 5, UA: 5, US: 5, UY: 5,
    CN: 6, CO: 6, IN: 6, NG: 6, RO: 6, RU: 6, SG: 6, VN: 6,
    CL: 7,
};

const RULES: Record<string, PostalCodeRule> = {
    US: {
        pattern: /^\d{5}(-\d{4})?$/,
        allowsLetters: false,
        maxLength: 10,
        charPattern: DIGITS_OR_HYPHEN,
    },
    BR: {
        pattern: /^\d{5}-?\d{3}$/,
        allowsLetters: false,
        maxLength: 9,
        charPattern: DIGITS_OR_HYPHEN,
    },
    JP: {
        pattern: /^\d{3}-?\d{4}$/,
        allowsLetters: false,
        maxLength: 8,
        charPattern: DIGITS_OR_HYPHEN,
    },
    PL: {
        pattern: /^\d{2}-\d{3}$/,
        allowsLetters: false,
        maxLength: 6,
        charPattern: DIGITS_OR_HYPHEN,
    },
    PT: {
        pattern: /^\d{4}-?\d{3}$/,
        allowsLetters: false,
        maxLength: 8,
        charPattern: DIGITS_OR_HYPHEN,
    },
    CZ: {
        pattern: /^\d{3} ?\d{2}$/,
        allowsLetters: false,
        maxLength: 6,
        charPattern: DIGITS_OR_SPACE,
    },
    SK: {
        pattern: /^\d{3} ?\d{2}$/,
        allowsLetters: false,
        maxLength: 6,
        charPattern: DIGITS_OR_SPACE,
    },
    SE: {
        pattern: /^\d{3} ?\d{2}$/,
        allowsLetters: false,
        maxLength: 6,
        charPattern: DIGITS_OR_SPACE,
    },
    GR: {
        pattern: /^\d{3} ?\d{2}$/,
        allowsLetters: false,
        maxLength: 6,
        charPattern: DIGITS_OR_SPACE,
    },
    NL: {
        pattern: /^\d{4} ?[A-Z]{2}$/i,
        allowsLetters: true,
        maxLength: 7,
        charPattern: ALPHANUMERIC,
    },
    CA: {
        pattern: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
        allowsLetters: true,
        maxLength: 7,
        charPattern: ALPHANUMERIC,
    },
    GB: {
        pattern: /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}|GIR ?0A{2})$/i,
        allowsLetters: true,
        maxLength: 8,
        charPattern: ALPHANUMERIC,
    },
    IE: {
        pattern: /^[AC-FHKNPRTV-Y]\d{2} ?[A-Z0-9]{4}$/i,
        allowsLetters: true,
        maxLength: 8,
        charPattern: ALPHANUMERIC,
    },
    MT: {
        pattern: /^[A-Z]{3} ?\d{4}$/i,
        allowsLetters: true,
        maxLength: 8,
        charPattern: ALPHANUMERIC,
    },
    AR: {
        pattern: /^([A-Z]\d{4}[A-Z]{3}|\d{4})$/i,
        allowsLetters: true,
        maxLength: 8,
        charPattern: ALPHANUMERIC,
    },
    AD: {
        pattern: /^AD\d{3}$/i,
        allowsLetters: true,
        maxLength: 5,
        charPattern: ALPHANUMERIC,
    },
    AZ: {
        pattern: /^AZ ?\d{4}$/i,
        allowsLetters: true,
        maxLength: 7,
        charPattern: ALPHANUMERIC,
    },
    BN: {
        pattern: /^[A-Z]{2}\d{4}$/i,
        allowsLetters: true,
        maxLength: 6,
        charPattern: ALPHANUMERIC,
    },
    LV: {
        pattern: /^LV-?\d{4}$/i,
        allowsLetters: true,
        maxLength: 7,
        charPattern: ALPHANUMERIC_HYPHEN,
    },
    LT: {
        pattern: /^LT-?\d{5}$/i,
        allowsLetters: true,
        maxLength: 8,
        charPattern: ALPHANUMERIC_HYPHEN,
    },
    MD: {
        pattern: /^MD-?\d{4}$/i,
        allowsLetters: true,
        maxLength: 7,
        charPattern: ALPHANUMERIC_HYPHEN,
    },
    GG: {
        pattern: /^GY\d[\dA-Z]? ?\d[A-Z]{2}$/i,
        allowsLetters: true,
        maxLength: 8,
        charPattern: ALPHANUMERIC,
    },
    IM: {
        pattern: /^IM\d[\dA-Z]? ?\d[A-Z]{2}$/i,
        allowsLetters: true,
        maxLength: 8,
        charPattern: ALPHANUMERIC,
    },
    JE: {
        pattern: /^JE\d[\dA-Z]? ?\d[A-Z]{2}$/i,
        allowsLetters: true,
        maxLength: 8,
        charPattern: ALPHANUMERIC,
    },
};

export function getPostalCodeRule(iso2?: string): PostalCodeRule {
    const code = (iso2 || '').trim().toUpperCase();
    if (!code) {
        return DEFAULT_RULE;
    }
    if (RULES[code]) {
        return RULES[code];
    }
    const length = NUMERIC_LENGTHS[code];
    if (length) {
        return numeric(length);
    }
    return DEFAULT_RULE;
}

export function sanitizePostalCode(value: string, iso2?: string): string {
    const rule = getPostalCodeRule(iso2);
    const source = value || '';
    let sanitized = '';
    for (const character of source) {
        if (rule.charPattern.test(character)) {
            sanitized += character;
        }
        if (sanitized.length >= rule.maxLength) {
            break;
        }
    }
    return sanitized;
}

export function isPostalCodeValid(value: string, iso2?: string): boolean {
    const trimmed = (value || '').trim();
    if (!trimmed) {
        return true;
    }
    return getPostalCodeRule(iso2).pattern.test(trimmed);
}

export function getPostalCodeMaxLength(iso2?: string): number {
    return getPostalCodeRule(iso2).maxLength;
}
